import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '../../context/ThemeContext';
import { useConversation } from '../../context/ConversationContext';
import { useProject } from '../../context/ProjectContext';

/**
 * CommandPalette - Spotlight-style command palette (SAT-83)
 *
 * Features:
 * - Opens with Cmd/Ctrl+K
 * - Search commands, conversations, and settings
 * - Keyboard navigation (arrow keys, Enter)
 * - Recent actions history
 * - Escape to close
 */

// Command definitions
const COMMANDS = [
  {
    id: 'new-chat',
    name: 'New Chat',
    description: 'Start a new conversation',
    icon: 'plus',
    category: 'Actions',
    keywords: ['create', 'new', 'conversation', 'chat'],
    action: (navigate) => navigate('/')
  },
  {
    id: 'toggle-theme',
    name: 'Toggle Dark Mode',
    description: 'Switch between light and dark mode',
    icon: 'moon',
    category: 'Appearance',
    keywords: ['theme', 'dark', 'light', 'mode', 'appearance'],
    action: (navigate, { toggleTheme }) => toggleTheme()
  },
  {
    id: 'settings',
    name: 'Open Settings',
    description: 'Configure application preferences',
    icon: 'cog',
    category: 'Navigation',
    keywords: ['preferences', 'config', 'options'],
    action: (navigate, { openSettings }) => openSettings()
  },
  {
    id: 'keyboard-shortcuts',
    name: 'Keyboard Shortcuts',
    description: 'View all keyboard shortcuts',
    icon: 'keyboard',
    category: 'Help',
    keywords: ['hotkeys', 'keys', 'shortcuts', 'help'],
    action: (navigate, { openKeyboardShortcuts }) => openKeyboardShortcuts()
  },
  {
    id: 'prompt-library',
    name: 'Prompt Library',
    description: 'Browse saved prompts',
    icon: 'book',
    category: 'Navigation',
    keywords: ['prompts', 'templates', 'saved'],
    action: (navigate, { openPromptLibrary }) => openPromptLibrary()
  },
  {
    id: 'feature-tour',
    name: 'Start Feature Tour',
    description: 'Take a guided tour of the app features',
    icon: 'tour',
    category: 'Help',
    keywords: ['tour', 'guide', 'help', 'learn', 'onboarding', 'tutorial'],
    action: (navigate, { startFeatureTour }) => startFeatureTour && startFeatureTour()
  }
];

// Icon components
const Icons = {
  plus: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  moon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
    </svg>
  ),
  cog: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  keyboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  book: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  chart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  chat: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  search: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  tour: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  )
};

function CommandPalette({ isOpen, onClose, onOpenSettings, onOpenKeyboardShortcuts, onOpenPromptLibrary, onStartFeatureTour }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentActions, setRecentActions] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { toggleTheme, isDark } = useTheme();
  const { conversations } = useConversation();

  // Load recent actions from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('commandPalette_recentActions');
    if (saved) {
      try {
        setRecentActions(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent actions:', e);
      }
    }
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Save recent action
  const addRecentAction = useCallback((actionId) => {
    setRecentActions(prev => {
      const filtered = prev.filter(id => id !== actionId);
      const updated = [actionId, ...filtered].slice(0, 5);
      localStorage.setItem('commandPalette_recentActions', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Filter and combine results
  const results = useMemo(() => {
    const items = [];
    const lowerQuery = query.toLowerCase().trim();

    // If no query, show recent actions first
    if (!lowerQuery) {
      // Recent commands
      if (recentActions.length > 0) {
        const recentCommands = recentActions
          .map(id => COMMANDS.find(c => c.id === id))
          .filter(Boolean)
          .map(cmd => ({ ...cmd, section: 'Recent' }));
        items.push(...recentCommands);
      }

      // All commands
      COMMANDS.forEach(cmd => {
        if (!recentActions.includes(cmd.id)) {
          items.push({ ...cmd, section: 'Commands' });
        }
      });

      // Recent conversations
      const conversationList = Array.isArray(conversations) ? conversations : [];
      const recentConvos = conversationList.slice(0, 5).map(conv => ({
        id: `conv-${conv.id}`,
        name: conv.title || 'Untitled conversation',
        description: `Last updated: ${new Date(conv.updated_at || conv.created_at).toLocaleDateString()}`,
        icon: 'chat',
        section: 'Recent Conversations',
        action: (nav) => nav(`/chat/${conv.id}`)
      }));
      items.push(...recentConvos);
    } else {
      // Search commands
      const matchingCommands = COMMANDS.filter(cmd => {
        const nameMatch = cmd.name.toLowerCase().includes(lowerQuery);
        const descMatch = cmd.description.toLowerCase().includes(lowerQuery);
        const keywordMatch = cmd.keywords.some(kw => kw.includes(lowerQuery));
        return nameMatch || descMatch || keywordMatch;
      }).map(cmd => ({ ...cmd, section: 'Commands' }));
      items.push(...matchingCommands);

      // Search conversations
      const searchableConvos = Array.isArray(conversations) ? conversations : [];
      const matchingConvos = searchableConvos
        .filter(conv =>
          (conv.title || '').toLowerCase().includes(lowerQuery) ||
          (conv.preview || '').toLowerCase().includes(lowerQuery)
        )
        .slice(0, 5)
        .map(conv => ({
          id: `conv-${conv.id}`,
          name: conv.title || 'Untitled conversation',
          description: conv.preview || `Last updated: ${new Date(conv.updated_at || conv.created_at).toLocaleDateString()}`,
          icon: 'chat',
          section: 'Conversations',
          action: (nav) => nav(`/chat/${conv.id}`)
        }));
      items.push(...matchingConvos);
    }

    return items;
  }, [query, conversations, recentActions]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results.length]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          executeAction(results[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [results, selectedIndex, onClose]);

  // Execute action
  const executeAction = useCallback((item) => {
    // Track recent action for commands
    if (!item.id.startsWith('conv-')) {
      addRecentAction(item.id);
    }

    // Execute the action
    item.action(navigate, {
      toggleTheme,
      openSettings: onOpenSettings,
      openKeyboardShortcuts: onOpenKeyboardShortcuts,
      openPromptLibrary: onOpenPromptLibrary,
      startFeatureTour: onStartFeatureTour
    });

    onClose();
  }, [navigate, toggleTheme, onOpenSettings, onOpenKeyboardShortcuts, onOpenPromptLibrary, onStartFeatureTour, onClose, addRecentAction]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Group results by section
  const sections = results.reduce((acc, item, index) => {
    const section = item.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push({ ...item, globalIndex: index });
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-100 flex items-start justify-center pt-[15vh] p-4 bg-black/50 backdrop-blur-xs modal-backdrop-transition animate-fadeIn"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-xl
          animate-fadeInScale gpu-accelerated
          border border-gray-200 dark:border-gray-700 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="text-gray-400">{Icons.search}</div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search commands, conversations..."
            className="flex-1 bg-transparent text-gray-900 dark:text-white
              placeholder-gray-400 dark:placeholder-gray-500
              outline-hidden text-base"
            aria-label="Search commands"
          />
          <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs
            font-medium text-gray-500 dark:text-gray-400
            bg-gray-100 dark:bg-gray-700 rounded">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          className="max-h-[50vh] overflow-y-auto py-2"
          role="listbox"
        >
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
              <p>No results found for "{query}"</p>
            </div>
          ) : (
            Object.entries(sections).map(([sectionName, items]) => (
              <div key={sectionName}>
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {sectionName}
                </div>
                {items.map((item) => (
                  <button
                    key={item.id}
                    data-index={item.globalIndex}
                    onClick={() => executeAction(item)}
                    onMouseEnter={() => setSelectedIndex(item.globalIndex)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left
                      transition-colors ${
                        item.globalIndex === selectedIndex
                          ? 'bg-decisiv-blue/10 text-decisiv-blue'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    role="option"
                    aria-selected={item.globalIndex === selectedIndex}
                  >
                    <div className={`shrink-0 ${
                      item.globalIndex === selectedIndex
                        ? 'text-decisiv-blue'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {Icons[item.icon] || Icons.search}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {item.description}
                      </div>
                    </div>
                    {item.globalIndex === selectedIndex && (
                      <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs
                        font-medium text-gray-500 dark:text-gray-400
                        bg-gray-100 dark:bg-gray-700 rounded">
                        Enter
                      </kbd>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700
          flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-sm">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-sm">↓</kbd>
              <span>Navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-sm">Enter</kbd>
              <span>Select</span>
            </span>
          </div>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-sm">
              {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-sm">K</kbd>
            <span>Open</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
